// src/components/templates/diary/CompetencyReport.jsx
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Award, TrendingUp, Target, CheckCircle, AlertCircle,
  BarChart3, Clock, BookOpen, Download, Star, Trophy, Zap
} from 'lucide-react';

const CompetencyReport = ({ bookId, courseId, userId, onBack, diaryService }) => {
  const [report, setReport] = useState(null);
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReportData();
  }, [bookId, courseId, userId]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const [courseData, enrollmentData, lessons] = await Promise.all([
        diaryService.getCourse(bookId, courseId),
        diaryService.getEnrollment(bookId, `${courseId}_${userId}`),
        diaryService.getLessons(bookId, courseId)
      ]);

      setCourse(courseData);
      setEnrollment(enrollmentData);

      // Generate report from enrollment data
      if (enrollmentData && courseData) {
        const generatedReport = {
          userId,
          courseId,
          courseName: courseData.title,
          overallProgress: enrollmentData.progress || 0,
          completedLessons: enrollmentData.completedLessons?.length || 0,
          totalLessons: lessons.length,
          competencyScores: enrollmentData.competencyScores || {},
          assessmentResults: enrollmentData.completedAssessments || {},
          totalTimeSpent: enrollmentData.totalTimeSpent || 0,
          completedAt: enrollmentData.progress >= 100 ? new Date() : null
        };
        setReport(generatedReport);
      }
    } catch (error) {
      console.error('Error loading report:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCompetencyLevel = (score) => {
    if (score >= 90) return { label: 'Expert', color: 'text-purple-600', bgColor: 'bg-purple-50' };
    if (score >= 80) return { label: 'Advanced', color: 'text-blue-600', bgColor: 'bg-blue-50' };
    if (score >= 70) return { label: 'Proficient', color: 'text-green-600', bgColor: 'bg-green-50' };
    if (score >= 60) return { label: 'Intermediate', color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
    return { label: 'Developing', color: 'text-orange-600', bgColor: 'bg-orange-50' };
  };

  const calculateOverallScore = () => {
    if (!report?.competencyScores || Object.keys(report.competencyScores).length === 0) {
      return 0;
    }

    const scores = Object.values(report.competencyScores).map(c => c.score);
    return scores.reduce((sum, score) => sum + score, 0) / scores.length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertCircle className="w-16 h-16 text-gray-400 mb-4" />
        <p className="text-gray-600">Failed to load competency report</p>
        <button onClick={onBack} className="mt-4 text-indigo-600 hover:text-indigo-700">
          Go Back
        </button>
      </div>
    );
  }

  const overallScore = calculateOverallScore();
  const overallLevel = getCompetencyLevel(overallScore);

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div>
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Competency Report</h1>
                  <p className="text-gray-600">{course?.title}</p>
                </div>
              </div>
              <button className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2">
                <Download className="w-4 h-4" /> Download PDF
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <StatBox
                icon={TrendingUp}
                label="Overall Progress"
                value={`${Math.round(report.overallProgress)}%`}
                color="blue"
              />
              <StatBox
                icon={BookOpen}
                label="Lessons Completed"
                value={`${report.completedLessons}/${report.totalLessons}`}
                color="green"
              />
              <StatBox
                icon={Target}
                label="Overall Score"
                value={`${Math.round(overallScore)}%`}
                color="purple"
              />
              <StatBox
                icon={Clock}
                label="Time Spent"
                value={`${Math.round((report.totalTimeSpent || 0) / 60)}h`}
                color="amber"
              />
            </div>
          </div>
        </div>

        {/* Overall Performance */}
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Overall Performance</h2>
          
          <div className="flex items-center gap-4 mb-6">
            <div className={`px-6 py-3 ${overallLevel.bgColor} ${overallLevel.color} rounded-lg font-semibold text-lg`}>
              {overallLevel.label}
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>Achievement Level</span>
                <span>{Math.round(overallScore)}%</span>
              </div>
              <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 transition-all"
                  style={{ width: `${overallScore}%` }}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <PerformanceCard
              icon={Trophy}
              label="Strengths"
              items={getTopCompetencies(report.competencyScores, true)}
              color="green"
            />
            <PerformanceCard
              icon={Zap}
              label="Moderate"
              items={getModerateCompetencies(report.competencyScores)}
              color="blue"
            />
            <PerformanceCard
              icon={Target}
              label="Focus Areas"
              items={getTopCompetencies(report.competencyScores, false)}
              color="orange"
            />
          </div>
        </div>

        {/* Competency Breakdown */}
        {Object.keys(report.competencyScores).length > 0 && (
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Competency Breakdown</h2>
            <div className="space-y-4">
              {Object.entries(report.competencyScores).map(([id, competency]) => {
                const level = getCompetencyLevel(competency.score);
                return (
                  <div key={id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{competency.name}</h3>
                        <p className="text-sm text-gray-600">
                          {competency.assessmentCount} assessment{competency.assessmentCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className={`px-3 py-1 ${level.bgColor} ${level.color} rounded-full text-sm font-medium`}>
                        {level.label}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              competency.score >= 80 ? 'bg-green-500' :
                              competency.score >= 70 ? 'bg-blue-500' :
                              competency.score >= 60 ? 'bg-yellow-500' : 'bg-orange-500'
                            }`}
                            style={{ width: `${competency.score}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 w-12 text-right">
                        {Math.round(competency.score)}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {report.recommendations && report.recommendations.length > 0 && (
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Recommendations</h2>
            <div className="space-y-3">
              {report.recommendations.map((rec, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 p-4 rounded-lg ${
                    rec.priority === 'high'
                      ? 'bg-red-50 border border-red-200'
                      : rec.priority === 'medium'
                      ? 'bg-yellow-50 border border-yellow-200'
                      : 'bg-blue-50 border border-blue-200'
                  }`}
                >
                  <AlertCircle className={`w-5 h-5 shrink-0 mt-0.5 ${
                    rec.priority === 'high'
                      ? 'text-red-600'
                      : rec.priority === 'medium'
                      ? 'text-yellow-600'
                      : 'text-blue-600'
                  }`} />
                  <div>
                    {rec.competency && (
                      <div className="font-medium text-gray-900 mb-1">{rec.competency}</div>
                    )}
                    <p className="text-gray-700 text-sm">{rec.message}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Certificate */}
        {report.overallProgress === 100 && (
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-8 text-white text-center">
            <Trophy className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Congratulations!</h2>
            <p className="text-indigo-100 mb-6">
              You've successfully completed this course
            </p>
            <button className="px-6 py-3 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 font-semibold">
              Download Certificate
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== SUB-COMPONENTS ====================

const StatBox = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600'
  };

  return (
    <div className="flex items-center gap-3">
      <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-600">{label}</div>
      </div>
    </div>
  );
};

const PerformanceCard = ({ icon: Icon, label, items, color }) => {
  const colorClasses = {
    green: 'bg-green-50 border-green-200 text-green-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700'
  };

  return (
    <div className={`border rounded-lg p-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5" />
        <h3 className="font-semibold">{label}</h3>
      </div>
      <ul className="space-y-2">
        {items.length > 0 ? (
          items.map((item, index) => (
            <li key={index} className="text-sm flex items-start gap-2">
              <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))
        ) : (
          <li className="text-sm opacity-75">None identified</li>
        )}
      </ul>
    </div>
  );
};

// Helper functions
const getTopCompetencies = (competencyScores, getHighest) => {
  const entries = Object.entries(competencyScores);
  if (entries.length === 0) return [];

  const sorted = entries.sort((a, b) => 
    getHighest ? b[1].score - a[1].score : a[1].score - b[1].score
  );

  return sorted
    .slice(0, 3)
    .filter(([, data]) => getHighest ? data.score >= 80 : data.score < 70)
    .map(([, data]) => data.name);
};

const getModerateCompetencies = (competencyScores) => {
  return Object.entries(competencyScores)
    .filter(([, data]) => data.score >= 70 && data.score < 80)
    .slice(0, 3)
    .map(([, data]) => data.name);
};

export default CompetencyReport;